import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, StarIcon, Timer, X, Layout } from 'lucide-react';
import { authenticatedFetch } from '../../utils/api';
import styles from './PresentationView.module.css';
import PauseAnalysis from '../PauseAnalysis/PauseAnalysis';
import { useDocumentHead } from '../../hooks/useDocumentHead';
import DeleteSection from '../DeleteSection/DeleteSection';
import ClarityAnalysis from '../ClarityAnalysis/ClarityAnalysis';
import PitchAnalysis from '../PitchAnalysis/PitchAnalysis';
import EnhancedAnalysis from '../EnhancedAnalysis/EnhancedAnalysis';
import CustomTooltip from '../Tooltip/Tooltip';
import PresentationCoach from '../PresentationCoach/PresentationCoach';
import Record from '../../pages/Record';
import RefinedTextSegment from '../RefinedTextSegment/RefinedTextSegment';

const PresentationView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [presentation, setPresentation] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [previousId, setPreviousId] = useState(id);
    const [videoOnlyPractice, setVideoOnlyPractice] = useState(false);

    useDocumentHead(
        `${presentation?.analysis?.suggested_title || presentation?.title || 'Presentation Analysis'} | TopSpeaks`,
        `Detailed AI analysis and feedback for ${presentation?.analysis?.suggested_title || presentation?.title || 'your presentation'}. Get insights on speaking patterns, topic coherence, and personalized recommendations.`,
        'index, follow'
    );

    useEffect(() => {
        const fetchPresentation = async () => {
            try {
                const data = await authenticatedFetch(`/api/presentation/presentations/${id}`);
                setPresentation(data);
                console.log("data:", data);
                // Check if there are no segments and set videoOnlyPractice flag
                if (!data.segments || data.segments.length === 0) {
                    setVideoOnlyPractice(true);
                } else {
                    setVideoOnlyPractice(false);
                }

            } catch (err) {
                if (err.status === 401) {
                    navigate('/login');
                } else if (err.status === 404) {
                    setError('Presentation not found');
                } else {
                    setError('Failed to load presentation');
                    console.error('Error:', err);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchPresentation();
    }, [id, navigate]);

    // Watch for changes in the URL parameter
    useEffect(() => {
        if (id !== previousId) {
            setActiveTab('overview');
            setPreviousId(id);
            setLoading(true); // Reset loading state for new presentation
        }
    }, [id, previousId]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderTranscriptSegment = (segment, index) => {
        if (segment.tags?.includes('disfluency')) {
            return (
                <span key={index} className={styles.disfluency} title="Filler word">
                    {segment.content}{' '}
                </span>
            );
        } else if (segment.tags?.includes('profanity')) {
            return (
                <span key={index} className={styles.profanity} title="Profanity">
                    {segment.content}{' '}
                </span>
            );
        } else if (segment.tags?.includes('low_clarity')) {
            return (
                <span key={index} className={styles.lowClarity} title={`Low clarity (${(segment.confidence * 100).toFixed(1)}%)`}>
                    {segment.content}{' '}
                </span>
            );
        }
        return <span key={index}>{segment.content} </span>;
    };

    const renderRefinedText = (refinedText) => {
        if (!refinedText) return null;

        // Icons for different tags
        const tagIcons = {
            emphasis: <StarIcon className={styles.tagIcon} size={12} />,
            pause: <Timer className={styles.tagIcon} size={12} />,
            remove: <X className={styles.tagIcon} size={12} />,
            structure: <Layout className={styles.tagIcon} size={12} />
        };

        // Tooltip messages for each tag type
        const tooltipMessages = {
            emphasis: 'This word or phrase is important. Add stress here by speaking slightly louder or slowing down.',
            pause: 'Pause briefly here to let your audience absorb what you just said or build anticipation.',
            remove: 'This word/phrase might dilute your message. Consider skipping it for a more concise delivery.',
            structure: 'Rephrasing this sentence could improve flow and make your message easier to follow.'
        };

        // Process the text and render with tags
        const processText = () => {
            const parts = refinedText.split(/(<\/?[^>]+>)/g);
            const result = [];
            let currentIndex = 0;

            for (let i = 0; i < parts.length; i++) {
                const openTagMatch = parts[i].match(/<(emphasis|pause|remove|structure)(?:\s+data-reason="([^"]*)")?>/);
                if (openTagMatch) {
                    const tagType = openTagMatch[1];
                    const reason = openTagMatch[2] || tooltipMessages[tagType];
                    const content = parts[i + 1];
                    const icon = tagIcons[tagType];

                    if (content && !content.includes('</')) {
                        result.push(
                            <RefinedTextSegment
                                key={`segment-${currentIndex}`}
                                tagType={tagType}
                                content={content}
                                reason={reason}
                                icon={icon}
                            />
                        );
                        currentIndex++;
                        i++;
                    }
                } else if (!parts[i].match(/<\/?[^>]+>/)) {
                    result.push(<span key={`text-${currentIndex}`}>{parts[i]}</span>);
                    currentIndex++;
                }
            }
            return result;
        };

        return (
            <div className={styles.refinedTextContainer}>
                <h2 className={styles.refinedTextTitle}>Refinement Guide</h2>
                <div className={styles.refinedText}>
                    {processText()}
                </div>
                <div className={styles.tagLegend}>
                    <h4 className={styles.legendTitle}>Tags Guide</h4>
                    <div className={styles.legendGrid}>
                        {Object.entries(tagIcons).map(([type, icon]) => (
                            <CustomTooltip
                                key={type}
                                text={tooltipMessages[type]}
                                isSegment={false}
                            >
                                <div className={styles.legendItem}>
                                    {icon}
                                    <span className={styles[`tag_${type}`]}>
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </span>
                                </div>
                            </CustomTooltip>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderAnalysisSection = () => {
        if (!presentation?.analysis) return null;
        const analysis = presentation.analysis;
        return (
            <div className={`${styles.analysisContainer} ${videoOnlyPractice ? styles.videoOnly : ''}`}>
                <div className={`${styles.refinementColumn} ${videoOnlyPractice ? styles.videoOnly : ''}`}>
                    {renderRefinedText(analysis.refined_text)}
                </div>
                <div className={styles.recordColumn}>
                    <Record isRehearsal={true} originalPresentationId={presentation?._id} />
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingState}>Loading presentation data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.errorContainer}>
                    <div className={styles.errorMessage}>{error}</div>
                    <Link to="/recordings" className={styles.returnLink}>
                        Return to Recordings
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <header className={styles.header}>
                    <div className={styles.headerLeft}>
                        <Link to="/recordings" className={styles.backButton}>
                            <ArrowLeft size={20} />
                            <span>Back to Recordings</span>
                        </Link>
                    </div>
                    <div className={styles.headerRight}>
                        <div className={styles.dateInfo}>
                            <Calendar size={16} />
                            <span>{formatDate(presentation.created_at)}</span>
                        </div>
                    </div>
                </header>
                <nav className={styles.tabs}>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'overview' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Overview
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'analysis-graphs' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('analysis-graphs')}
                    >
                        Analysis Graphs
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'improve' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('improve')}
                    >
                        Improve
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'ask-coach' ? styles.activeTab : ''}`}
                        onClick={() => setActiveTab('ask-coach')}
                    >
                        AI Coach
                    </button>
                </nav>
                <main className={styles.mainContent}>

                    {/* Analysis Tabs Section */}
                    <div className={styles.analysisSection}>
                        <div className={styles.tabContent}>
                            {activeTab === 'overview' && (
                                <>
                                    {/* Transcript Section */}
                                    <div className={styles.transcriptSection}>
                                        <div className={styles.transcriptCard}>
                                            <div className={styles.transcript}>
                                                {presentation.segments
                                                    ? presentation.segments.map((segment, index) =>
                                                        renderTranscriptSegment(segment, index))
                                                    : presentation.transcript
                                                }
                                            </div>
                                            {presentation.segments && (
                                                <div className={styles.legend}>
                                                    <div className={styles.legendItem}>
                                                        <span className={styles.disfluencyDot} />
                                                        Filler Words
                                                    </div>
                                                    <div className={styles.legendItem}>
                                                        <span className={styles.profanityDot} />
                                                        Profanity
                                                    </div>
                                                    <div className={styles.legendItem}>
                                                        <span className={styles.lowClarityDot} />
                                                        Low Clarity Words
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <EnhancedAnalysis
                                        enhancedAnalysis={presentation.enhanced_analysis}
                                        rehearsalData={presentation.rehearsal_data}
                                        onTabChange={setActiveTab}
                                        videoOnlyPractice={videoOnlyPractice}
                                    />
                                </>
                            )}
                            {activeTab === 'improve' && renderAnalysisSection()}
                            {activeTab === 'analysis-graphs' && (
                                <div className={styles.analysisGraphs}>
                                    {videoOnlyPractice ? (
                                        <p>Available when advanced analytics are active.</p>
                                    ) : (
                                        <>
                                            <PitchAnalysis environment={presentation.enhanced_analysis.environment} ranges={presentation.enhanced_analysis.optimal_ranges} segments={presentation.segments} />
                                            <ClarityAnalysis segments={presentation.segments} />
                                            <PauseAnalysis environment={presentation.enhanced_analysis.environment} segments={presentation.segments} />
                                        </>
                                    )}
                                </div>
                            )}
                            {activeTab === 'ask-coach' && (
                                <PresentationCoach presentationId={id} videoOnlyPractice={videoOnlyPractice} />
                            )}
                        </div>
                    </div>

                    <DeleteSection presentationId={id} />
                </main>
            </div>
        </div>
    );
};

export default PresentationView;